import { useParams, useRouter } from 'next/navigation';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import { signOut, useSession } from 'next-auth/react';
import useRepositoryStore from '@/state/repositoryState';
import emptyRepository from '@/types/emptyRepository';
import { mountRepoURL } from '@/helpers/repoInfoExtracter';
import useSelectedStore from '@/state/selectedState';
import useLoadingStore from '@/state/loadingState';
import emptyOrganization from '@/types/emptyOrganization';
import Organization from '@/types/organizationType';
import User from '@/types/userType';
import { useEffect } from 'react';
import { useArtifactStore } from '@/state/useArtifactState';
import { transformRepoName } from '@/lib/utils/repo';
import { useAuthStore } from '@/state/authState';
import { getFrontUrl } from '@/lib/auth/frontUrl';
import { fetchUserByProviderUsername } from '@/apis/fetchUser';

const frontURL = getFrontUrl();

/**
 * One bootstrap per page load, shared across every hook instance.
 *
 * `InitialSpinner` fires `updateData` from a mount effect, and one page load
 * can mount two of them: `RequireSession` renders it as its Suspense fallback,
 * and when the session check resolves the route's own gate renders a fresh one,
 * because `isInitalFetchDone` has not flipped yet. Those are two separate
 * components with two separate hook instances, so this latch cannot live in a
 * ref. Module scope is what lets the second mount see the first one's run and
 * join it instead of issuing the whole user + org + repositories chain again.
 *
 * Cleared when the run settles, so a later remount (a sign-in, or anything that
 * resets the loading flags) still bootstraps normally.
 */
let bootstrapInFlight: Promise<void> | null = null;

export const useUpdatedData = () => {
  const { repoName } = useParams();
  const { data: session } = useSession();
  const repoInfo = transformRepoName(repoName as string);
  const router = useRouter();
  const { authUrl } = useAuthStore();
  const updateUserAndOrganizationData = async (): Promise<
    { user: User; org: Organization } | undefined
  > => {
    const setUser = await useUserStore.getState().setUser;
    const setOrganizationByName =
      useOrganizationStore.getState().setOrganizationByName;
    const setOrganizationByUUID =
      useOrganizationStore.getState().setOrganizationByUUID;
    const organization = useOrganizationStore.getState().organization;

    if (session?.user?.email) {
      const orgName = localStorage.getItem('OrgName');
      const orgUUID =
        useUserStore?.getState()?.user?.OrganizationUUID ||
        useOrganizationStore.getState().organization?.OrgUUID;
      const sessionUser = session.user as {
        id?: string;
        email?: string | null;
        username?: string;
        ghUserName?: string;
        provider?: string;
      };

      try {
        // GitHub only: numeric account id first so a drifted IdP email still
        // resolves the same row and bootstrap gets a real user. Other
        // providers keep the email path.
        const resolveUser = async () => {
          if ((sessionUser.provider ?? '').toLowerCase() === 'github') {
            const byGithub = await fetchUserByProviderUsername(
              sessionUser.username ?? sessionUser.ghUserName ?? '',
              sessionUser.provider,
              {},
              sessionUser.id
            );
            if (byGithub?.UserUUID) {
              useUserStore.getState().setPureUser(byGithub);
              return byGithub;
            }
          }
          return setUser(
            sessionUser.email || localStorage.getItem('UserEmail') || ''
          );
        };

        const [user, org] = await Promise.all([
          resolveUser(),
          orgName
            ? setOrganizationByName(orgName)
            : setOrganizationByUUID(orgUUID),
        ]);

        return {
          user,
          org,
        };
      } catch (err) {
        signOut({ callbackUrl: `${frontURL}/login` });
      }
    } else {
      localStorage.setItem('LoggedIn', '0');
    }
  };

  const setOrgOnSignIn = async (OrgUUID: string) => {
    const setOrganizationByUUID =
      useOrganizationStore.getState().setOrganizationByUUID;
    return Promise.all([setOrganizationByUUID(OrgUUID)]);
  };

  const updateRepositoryAndConversations = async () => {
    const user = useUserStore.getState().user;
    const organization = useOrganizationStore.getState().organization;
    const setOrganizationRepositoryList =
      useRepositoryStore.getState().setOrganizationRepositoryList;
    const setUserRepositoryList =
      useRepositoryStore.getState().setUserRepositoryList;

    return Promise.all([
      setUserRepositoryList(user?.UserUUID),
      setOrganizationRepositoryList(organization?.OrgUUID),
    ]);
  };

  const validateUserOnboardind = async (user: User | undefined) => {
    if (session?.user && !user?.UserUUID) {
      const sessionUser = session.user as {
        id?: string;
        username?: string;
        ghUserName?: string;
        provider?: string;
      };
      // GitHub-only recovery: IdP placeholders break email lookup. Other
      // providers still treat a miss as a new user.
      if ((sessionUser.provider ?? '').toLowerCase() === 'github') {
        const recovered = await fetchUserByProviderUsername(
          sessionUser.username ?? sessionUser.ghUserName ?? '',
          sessionUser.provider,
          {},
          sessionUser.id
        );
        if (recovered?.UserUUID) {
          useUserStore.getState().setPureUser(recovered);
          if (recovered.OrganizationUUID) {
            await setOrgOnSignIn(recovered.OrganizationUUID);
          }
          useLoadingStore.getState().setIsInitialFetchDone(true);
          useLoadingStore.getState().setIsInitialRendered(true);
          return;
        }
      }
      router.push('/onboarding');
      return;
    }

    useLoadingStore.getState().setIsInitialFetchDone(true);
    useLoadingStore.getState().setIsInitialRendered(true);
  };

  const runUpdateData = async () => {
    const response = await updateUserAndOrganizationData();

    useArtifactStore.getState().setOpenArtifact(false);

    if (localStorage.getItem('LoggedIn') === '0' || !response?.user) {
      const currentRepo = emptyRepository;
      currentRepo.Name = repoInfo?.repoName ?? '';
      currentRepo.Url =
        mountRepoURL(repoInfo?.repoName, session?.user?.provider, authUrl) ??
        '';
      currentRepo.VectorDBURL = repoInfo?.vectorDBURL ?? '';

      const setUserRepositoryListByParam =
        useRepositoryStore.getState().setUserRepositoryListByParam;
      if (currentRepo.Name !== 'dashboard/undefined')
        setUserRepositoryListByParam(currentRepo);
      const setSelectedRepository =
        useSelectedStore.getState().setSelectedRepository;
      await setSelectedRepository(currentRepo, true);

      useLoadingStore.getState().setIsInitialFetchDone(true);
      useLoadingStore.getState().setIsInitialRendered(true);
      await validateUserOnboardind(response?.user);
    } else {
      const user = useUserStore.getState().user;
      const organization =
        localStorage.getItem('IndividualLogin') === '1'
          ? emptyOrganization
          : useOrganizationStore.getState().organization;

      if (organization === emptyOrganization && user?.OrganizationUUID !== '') {
        await setOrgOnSignIn(user?.OrganizationUUID);
      }

      // Deliberately not awaited. Around thirty dashboard routes render nothing
      // until isInitalFetchDone flips, and the repository lists gate none of
      // them: their readers are the chat, docs, settings and onboarding
      // surfaces, plus AuthProvider, which reacts to the store and re-runs when
      // the lists land. Awaiting them here made every page wait on two requests
      // it never reads before it could issue its own.
      void updateRepositoryAndConversations();
      useArtifactStore.getState().setOpenArtifact(false);

      useLoadingStore.getState().setIsInitialFetchDone(true);
      useLoadingStore.getState().setIsInitialRendered(true);
      await validateUserOnboardind(response?.user);
    }
  };

  // Deduped against `bootstrapInFlight` rather than run directly, so the two
  // InitialSpinner mounts a gated route can produce share one bootstrap.
  const updateData: any = async () => {
    bootstrapInFlight ??= runUpdateData().finally(() => {
      bootstrapInFlight = null;
    });
    return bootstrapInFlight;
  };

  const updateDataWithoutCreateFirstRepo = async () => {
    const { user, org } = (await updateUserAndOrganizationData()) || {};

    if (!user) {
      return;
    }

    useArtifactStore.getState().setOpenArtifact(false);

    if (localStorage.getItem('LoggedIn') === '0' || !user) {
      useLoadingStore.getState().setIsInitialFetchDone(true);
      useLoadingStore.getState().setIsInitialRendered(true);
      await validateUserOnboardind(user);
    }

    // Not awaited, for the reason given in updateData above.
    void updateRepositoryAndConversations();
    useArtifactStore.getState().setOpenArtifact(false);
    useLoadingStore.getState().setIsInitialFetchDone(true);
    useLoadingStore.getState().setIsInitialRendered(true);
  };

  useEffect(() => {
    if (session?.user?.email) {
      localStorage.setItem('UserEmail', session.user.email);
      localStorage.setItem('LoggedIn', '1');
    }
  }, [session]);

  return {
    updateData,
    updateUserAndOrganizationData,
    setOrgOnSignIn,
    updateRepositoryAndConversations,
    validateUserOnboardind,
    updateDataWithoutCreateFirstRepo,
  };
};
